import React from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Button
} from '@mui/material';
import {
  Lock as LockIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { colors } from '../../styles/theme';

interface AccessDeniedPageProps {
  requiredPermission?: string;
  /**
   * Der Bereich, für den der Zugriff fehlt (z.B. „Kalkulation › Kosten").
   *
   * Wird statt des Auth0-Scopes angezeigt, wo es um Projektzugriff geht: Ein externer Nutzer
   * braucht keine Permission, sondern eine Freigabe – der Scope-Name wäre dort die falsche
   * Auskunft und führte zu einer Bitte, die niemand erfüllen sollte.
   */
  bereichName?: string;
  pageName?: string;
  onBack?: () => void;
}

/**
 * Zeigt eine Fehlermeldung an, wenn der Benutzer keine Berechtigung für eine Seite hat
 */
const AccessDeniedPage: React.FC<AccessDeniedPageProps> = ({
  requiredPermission,
  bereichName,
  pageName,
  onBack
}) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: 'calc(100vh - 200px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8
        }}
      >
        <Paper
          elevation={2}
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 3,
            maxWidth: 500,
            width: '100%'
          }}
        >
          {/* Icon */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 3
            }}
          >
            <LockIcon
              sx={{
                fontSize: 64,
                color: colors.secondary.main,
                opacity: 0.8
              }}
            />
          </Box>

          {/* Titel */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 300,
              color: 'text.primary',
              mb: 2
            }}
          >
            Zugriff verweigert
          </Typography>

          {/* Beschreibung */}
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              mb: 4,
              lineHeight: 1.6
            }}
          >
            {pageName ? (
              <>
                Sie haben keine Berechtigung, die Seite <strong>"{pageName}"</strong> zu betreten.
              </>
            ) : (
              'Sie haben keine Berechtigung, diese Seite zu betreten.'
            )}
          </Typography>

          {/* Fehlender Zugriff: Bereich (Projektzugriff) oder Auth0-Scope (Admin-Bereiche) */}
          {(bereichName || requiredPermission) && (
            <Box
              sx={{
                backgroundColor: colors.background.tableHeader,
                borderRadius: 2,
                p: 3,
                mb: 4
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                  mb: 1
                }}
              >
                {bereichName ? 'Nicht freigegebener Bereich:' : 'Erforderliche Berechtigung:'}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: colors.primary.main,
                  fontWeight: 600,
                  fontFamily: bereichName ? undefined : 'monospace'
                }}
              >
                {bereichName ?? requiredPermission}
              </Typography>
            </Box>
          )}

          {/* Zurück-Button */}
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{
              minWidth: 160,
              py: 1.5,
              fontSize: '1rem'
            }}
          >
            Zurück
          </Button>
        </Paper>
      </Box>
    </Container>
  );
};

export default AccessDeniedPage;
